import { type FormEvent, useCallback, useEffect, useState } from 'react';

import {
  activateGuidelinesDraft,
  ApiError,
  discardGuidelinesDraft,
  fetchGuidelines,
  fetchGuidelinesDecks,
  fetchGuidelinesDiff,
  fetchGuidelinesDraft,
  GUIDELINES_SECTION_KEYS,
  type GuidelinesBannedRemoval,
  type GuidelinesDecksView,
  type GuidelinesDraftResult,
  type GuidelinesOverview,
  type GuidelinesSectionDiff,
  type GuidelinesSectionKey,
  type GuidelinesSections,
  type GuidelinesVersion,
  type GuidelinesVersionSummary,
  saveGuidelinesDraft,
  takeGuidelinesSeed,
  undoGuidelines,
} from './api.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { describeError } from './data.tsx';
import { Icon } from './Icon.tsx';
import { useToast } from './toast.tsx';
import { Badge, Markdown, Notice, Panel, Skeleton } from './ui.tsx';

/**
 * Settings → **Guidelines** (the ad bible US-032): read, edit, version and undo
 * the bible every writing and judging prompt is given, without a deploy.
 *
 * The pattern is the format presets panel's (`RulesPanel.tsx`): the server
 * checks every document against the same schema the boot checks the shipped
 * files with, and the panel shows its sentences rather than deciding anything
 * itself. What is different is that an edit is never live on its own: it goes
 * into a **draft**, and only **Activate** (with a change note) makes the draft
 * the version the prompts read. **Undo** steps the active version back one.
 *
 * A section the operator edited is never overwritten by a newer build: it is
 * marked "newer seed available", and **Take newer seed** puts the shipped text
 * into the draft, to be read in the diff and activated like any other edit.
 */

const SECTION_LABELS: Record<GuidelinesSectionKey, string> = {
  charter: 'Charter',
  message: 'Message',
  hooks: 'Hooks',
  proof: 'Proof and claims',
  psychology: 'Psychology',
  lines: 'Lines',
  formats: 'Formats and series',
  banned: 'Banned',
  share_copy: 'Share copy',
  rubric: 'Rubric',
  style: 'Style',
  imagery: 'Imagery',
  examples: 'Examples',
  decks: 'Decks',
  audiences: 'Audiences',
};

/** What a section is edited as: its markdown, or JSON the schema checks. */
function isJsonSection(key: GuidelinesSectionKey): key is 'decks' | 'audiences' {
  return key === 'decks' || key === 'audiences';
}

function describeGuidelines(view: GuidelinesOverview): string {
  if (view.active === null) return 'no version yet';
  const parts = [`v${String(view.active.number)} active`];
  if (view.draft !== null) parts.push(`draft v${String(view.draft.number)}`);
  if (view.pending.length > 0)
    parts.push(`${String(view.pending.length)} newer seed${view.pending.length === 1 ? '' : 's'}`);
  return parts.join(', ');
}

/** Stable JSON: whether a section of the draft differs from the active one. */
function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function when(iso: string | null): string {
  return iso === null ? '—' : new Date(iso).toLocaleString();
}

/** The section being edited: the markdown, or the JSON, as typed. */
interface Editing {
  key: GuidelinesSectionKey;
  /** Markdown, for every section but decks and audiences (for banned: its text). */
  text: string;
  /** JSON: banned's rules file, or the decks or audiences record. */
  json: string;
}

export function GuidelinesPanel() {
  const toast = useToast();
  const [view, setView] = useState<GuidelinesOverview | null>(null);
  const [draft, setDraft] = useState<GuidelinesVersion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState<GuidelinesSectionKey | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [removed, setRemoved] = useState<readonly GuidelinesBannedRemoval[]>([]);
  const [note, setNote] = useState('');
  const [diff, setDiff] = useState<{ from: number; to: number; sections: GuidelinesSectionDiff[] } | null>(null);
  const [confirming, setConfirming] = useState<'discard' | 'undo' | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (signal?: AbortSignal): Promise<void> => {
    const next = await fetchGuidelines(signal);
    setView(next);
    setDraft(next.draft === null ? null : await fetchGuidelinesDraft(signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setError(describeError(cause));
    });
    return () => {
      controller.abort();
    };
  }, [reload]);

  /** Runs a write, reloads the overview, and shows what went wrong in the panel. */
  const run = (work: () => Promise<void>): void => {
    setBusy(true);
    setError(null);
    work()
      .then(() => reload())
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.message : describeError(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const base: GuidelinesSections | null = draft?.sections ?? view?.active?.sections ?? null;

  const edit = (key: GuidelinesSectionKey): void => {
    if (base === null) return;
    setProblems([]);
    setRemoved([]);
    setError(null);
    if (key === 'banned') {
      setEditing({ key, text: base.banned.text, json: JSON.stringify(base.banned.rules, null, 2) });
    } else if (isJsonSection(key)) {
      setEditing({ key, text: '', json: JSON.stringify(base[key], null, 2) });
    } else {
      setEditing({ key, text: base[key], json: '' });
    }
  };

  const afterWrite = (result: GuidelinesDraftResult, done: string): boolean => {
    if (!result.ok) {
      setProblems(result.problems);
      return false;
    }
    setDraft(result.draft);
    setRemoved(result.bannedRemovals);
    toast.ok(done);
    return true;
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (editing === null) return;
    let value: unknown = editing.text;
    if (editing.key === 'banned' || isJsonSection(editing.key)) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(editing.json);
      } catch (cause) {
        setProblems([`This is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`]);
        return;
      }
      value = editing.key === 'banned' ? { text: editing.text, rules: parsed } : parsed;
    }
    const key = editing.key;
    setProblems([]);
    run(async () => {
      const result = await saveGuidelinesDraft({ [key]: value });
      if (afterWrite(result, `${SECTION_LABELS[key]} saved to the draft. Activate it to make it live.`))
        setEditing(null);
    });
  };

  const takeSeed = (key: GuidelinesSectionKey): void => {
    setProblems([]);
    run(async () => {
      const result = await takeGuidelinesSeed(key);
      if (!result.ok) {
        setError(`The shipped ${SECTION_LABELS[key]} was refused: ${result.problems.join(' ')}`);
        return;
      }
      afterWrite(result, `The shipped ${SECTION_LABELS[key]} is in the draft. Compare and activate it to take it.`);
    });
  };

  const activate = (event: FormEvent): void => {
    event.preventDefault();
    const text = note.trim();
    if (text === '') {
      setError('Say what changed and why: activating needs a change note.');
      return;
    }
    run(async () => {
      const active = await activateGuidelinesDraft(text);
      setNote('');
      setRemoved([]);
      setDiff(null);
      toast.ok(`Guidelines v${String(active.number)} are live. The next prompt reads them.`);
    });
  };

  const compare = (from: number, to: number): void => {
    run(async () => {
      setDiff({ from, to, sections: await fetchGuidelinesDiff(from, to) });
    });
  };

  const confirm = (): void => {
    const action = confirming;
    setConfirming(null);
    if (action === 'discard') {
      run(async () => {
        await discardGuidelinesDraft();
        setEditing(null);
        setDiff(null);
        toast.ok('The draft is discarded.');
      });
    } else if (action === 'undo') {
      run(async () => {
        const active = await undoGuidelines();
        setDiff(null);
        toast.ok(`Back to guidelines v${String(active.number)}.`);
      });
    }
  };

  const pending = new Map(view?.pending.map((entry) => [entry.key, entry.inDraft]) ?? []);
  const active = view?.active ?? null;

  return (
    <Panel
      id="guidelines"
      title="Guidelines"
      icon="tasklist"
      collapsible
      defaultOpen={false}
      summary={view === null ? (error === null ? 'loading…' : 'could not load') : describeGuidelines(view)}
    >
      <p className="muted">
        The ad bible: what every script, hook, line and picture is written and judged against. An edit
        goes into a draft; nothing changes for the prompts until the draft is activated.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {removed.length > 0 && (
        <Notice kind="warn">
          <p>
            <strong>This draft drops banned rules.</strong> The lint stops catching these once it is
            activated:
          </p>
          <ul className="rules-list">
            {removed.map((removal) => (
              <li key={`${removal.rule}:${removal.pattern ?? ''}`}>
                <code>{removal.rule}</code>
                {removal.pattern !== undefined && (
                  <>
                    {' '}
                    pattern <code>{removal.pattern}</code>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      {view === null || active === null ? (
        view === null ? (
          <Skeleton lines={4} />
        ) : (
          <Notice kind="info">There is no version of the guidelines yet: the next boot seeds it.</Notice>
        )
      ) : (
        <>
          {view.draft !== null && (
            <form className="guidelines-draft" onSubmit={activate}>
              <p>
                <Badge tone="review">draft v{String(view.draft.number)}</Badge> Not live yet: the prompts
                still read v{String(active.number)}.
              </p>
              <div className="field">
                <label className="field__label" htmlFor="guidelines-note">
                  Change note
                </label>
                <input
                  id="guidelines-note"
                  className="field__input"
                  value={note}
                  placeholder="What changed, and why"
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                />
              </div>
              <div className="form__actions">
                <button className="button button--primary" type="submit" disabled={busy || note.trim() === ''}>
                  Activate
                </button>
                <button
                  className="button"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (view.draft !== null) compare(active.number, view.draft.number);
                  }}
                >
                  Compare with v{String(active.number)}
                </button>
                <button
                  className="button"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setConfirming('discard');
                  }}
                >
                  Discard draft
                </button>
              </div>
            </form>
          )}

          <section className="rules-table">
            <header className="rules-table__header">
              <h3 className="rules-table__title">Sections of v{String(active.number)}</h3>
            </header>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Section</th>
                    <th scope="col">State</th>
                    <th scope="col" className="table__actions">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {GUIDELINES_SECTION_KEYS.map((key) => (
                    <tr key={key}>
                      <td>
                        {SECTION_LABELS[key]} <code className="muted">{key}</code>
                      </td>
                      <td>
                        {draft !== null && !same(draft.sections[key], active.sections[key]) && (
                          <Badge tone="review" title="The draft holds another text for this section.">
                            changed in draft
                          </Badge>
                        )}{' '}
                        {pending.has(key) && (
                          <Badge
                            tone="wait"
                            title="This build ships a newer text for a section you edited. It is not taken until you take it."
                          >
                            {pending.get(key) === true ? 'newer seed in draft' : 'newer seed available'}
                          </Badge>
                        )}
                      </td>
                      <td className="table__actions">
                        <button
                          className="button button--small"
                          type="button"
                          aria-expanded={reading === key}
                          onClick={() => {
                            setReading(reading === key ? null : key);
                          }}
                        >
                          {reading === key ? 'Hide' : 'Read'}
                        </button>{' '}
                        <button
                          className="button button--small"
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            edit(key);
                          }}
                        >
                          Edit
                        </button>
                        {pending.has(key) && pending.get(key) !== true && (
                          <>
                            {' '}
                            <button
                              className="button button--small"
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                takeSeed(key);
                              }}
                            >
                              Take newer seed
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {reading !== null && (
            <section className="guidelines-section" aria-label={SECTION_LABELS[reading]}>
              <h3 className="rules-table__title">
                {SECTION_LABELS[reading]}, as v{String(active.number)} has it
              </h3>
              <SectionView sectionKey={reading} sections={active.sections} />
            </section>
          )}

          {editing !== null && (
            <form className="form" onSubmit={submit}>
              <h3 className="rules-table__title">Edit {SECTION_LABELS[editing.key]} in the draft</h3>
              {!isJsonSection(editing.key) && (
                <div className="field">
                  <label className="field__label" htmlFor="guidelines-text">
                    Markdown
                  </label>
                  <textarea
                    id="guidelines-text"
                    className="field__input field__input--code"
                    rows={18}
                    value={editing.text}
                    onChange={(event) => {
                      setEditing({ ...editing, text: event.target.value });
                    }}
                  />
                </div>
              )}
              {(editing.key === 'banned' || isJsonSection(editing.key)) && (
                <div className="field">
                  <label className="field__label" htmlFor="guidelines-json">
                    {editing.key === 'banned' ? (
                      <>
                        The rules, as <code>banned.json</code>
                      </>
                    ) : (
                      <>The {editing.key}, as JSON</>
                    )}
                  </label>
                  <textarea
                    id="guidelines-json"
                    className="field__input field__input--code"
                    rows={18}
                    spellCheck={false}
                    value={editing.json}
                    aria-invalid={problems.length > 0}
                    aria-describedby={problems.length > 0 ? 'guidelines-problems' : undefined}
                    onChange={(event) => {
                      setEditing({ ...editing, json: event.target.value });
                    }}
                  />
                </div>
              )}
              {problems.length > 0 && (
                <Notice kind="error">
                  <div id="guidelines-problems">
                    <p>
                      <strong>Not saved.</strong> The guidelines would not be sound:
                    </p>
                    <ul className="rules-list">
                      {problems.map((problem) => (
                        <li key={problem}>{problem}</li>
                      ))}
                    </ul>
                  </div>
                </Notice>
              )}
              <div className="form__actions">
                <button className="button button--primary" type="submit" disabled={busy}>
                  {busy ? 'Saving…' : 'Save to draft'}
                </button>
                <button
                  className="button"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setEditing(null);
                    setProblems([]);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <History
            history={view.history}
            canUndo={view.canUndo}
            busy={busy}
            onCompare={compare}
            onUndo={() => {
              setConfirming('undo');
            }}
          />

          {diff !== null && (
            <DiffView
              diff={diff}
              onClose={() => {
                setDiff(null);
              }}
            />
          )}

          <DecksView />
        </>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={confirming === 'discard' ? 'Discard the draft?' : 'Undo the active guidelines?'}
        confirmLabel={confirming === 'discard' ? 'Discard' : 'Undo'}
        danger={confirming === 'discard'}
        busy={busy}
        onConfirm={confirm}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        {confirming === 'discard' ? (
          <p>Every edit in the draft is dropped. The active version is not touched.</p>
        ) : (
          <p>
            The version before v{String(active?.number ?? 0)} becomes the one the prompts read again. The
            current one stays in the history.
          </p>
        )}
      </ConfirmDialog>
    </Panel>
  );
}

/** One section, read: markdown rendered, the JSON parts as a summary and the file. */
function SectionView({
  sectionKey,
  sections,
}: {
  readonly sectionKey: GuidelinesSectionKey;
  readonly sections: GuidelinesSections;
}) {
  if (sectionKey === 'banned') {
    const rules = (sections.banned.rules as { rules?: unknown[] } | null)?.rules ?? [];
    return (
      <>
        <Markdown text={sections.banned.text} />
        <details>
          <summary>
            {String(rules.length)} rule{rules.length === 1 ? '' : 's'} in <code>banned.json</code>
          </summary>
          <pre className="output">{JSON.stringify(sections.banned.rules, null, 2)}</pre>
        </details>
      </>
    );
  }
  if (sectionKey === 'audiences') {
    return (
      <>
        {Object.values(sections.audiences).map((audience) => (
          <details key={audience.key}>
            <summary>
              {audience.name} <code>{audience.key}</code>
            </summary>
            <Markdown text={audience.text} />
          </details>
        ))}
      </>
    );
  }
  if (sectionKey === 'decks') {
    return (
      <ul className="rules-list">
        {Object.entries(sections.decks).map(([name, deck]) => (
          <li key={name}>
            <code>{name}</code> {String((deck as { description?: unknown }).description ?? '')}
          </li>
        ))}
      </ul>
    );
  }
  return <Markdown text={sections[sectionKey]} />;
}

/** Every version, newest first: compare one with its parent, and undo the active one. */
function History({
  history,
  canUndo,
  busy,
  onCompare,
  onUndo,
}: {
  readonly history: readonly GuidelinesVersionSummary[];
  readonly canUndo: boolean;
  readonly busy: boolean;
  readonly onCompare: (from: number, to: number) => void;
  readonly onUndo: () => void;
}) {
  return (
    <section className="rules-table">
      <header className="rules-table__header">
        <h3 className="rules-table__title">
          <Icon name="history" /> History
        </h3>
      </header>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Version</th>
              <th scope="col">Change note</th>
              <th scope="col">Made active</th>
              <th scope="col" className="table__actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {history.map((row) => {
              const previous = row.parentNumber ?? (row.number > 1 ? row.number - 1 : null);
              return (
                <tr key={row.number}>
                  <td>
                    v{String(row.number)}{' '}
                    {row.status !== 'retired' && (
                      <Badge tone={row.status === 'active' ? 'done' : 'review'}>{row.status}</Badge>
                    )}
                  </td>
                  <td className="muted">{row.changeNote ?? '—'}</td>
                  <td className="muted">{when(row.approvedAt)}</td>
                  <td className="table__actions">
                    {previous !== null && (
                      <button
                        className="button button--small"
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          onCompare(previous, row.number);
                        }}
                      >
                        Diff with v{String(previous)}
                      </button>
                    )}
                    {row.status === 'active' && canUndo && (
                      <>
                        {' '}
                        <button className="button button--small" type="button" disabled={busy} onClick={onUndo}>
                          Undo
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Lines of unchanged context kept around a change; longer runs fold. */
const CONTEXT = 3;

function DiffView({
  diff,
  onClose,
}: {
  readonly diff: { from: number; to: number; sections: readonly GuidelinesSectionDiff[] };
  readonly onClose: () => void;
}) {
  return (
    <section className="rules-table">
      <header className="rules-table__header">
        <h3 className="rules-table__title">
          v{String(diff.from)} → v{String(diff.to)}
        </h3>
        <button className="button button--small" type="button" onClick={onClose}>
          <Icon name="x" /> Close
        </button>
      </header>
      {diff.sections.length === 0 ? (
        <p className="muted">The two versions hold the same text.</p>
      ) : (
        diff.sections.map((section) => (
          <details key={section.key} open>
            <summary>
              {SECTION_LABELS[section.key]}{' '}
              <span className="guidelines-diff__count">
                +{String(section.added)} −{String(section.removed)}
              </span>
            </summary>
            <pre className="output guidelines-diff">
              {foldContext(section.lines).map((line, index) =>
                line === null ? (
                  <span key={index} className="guidelines-diff__fold">
                    {'…\n'}
                  </span>
                ) : (
                  <span key={index} className={`guidelines-diff__${line.op}`}>
                    {line.op === 'add' ? '+ ' : line.op === 'remove' ? '- ' : '  '}
                    {line.text}
                    {'\n'}
                  </span>
                ),
              )}
            </pre>
          </details>
        ))
      )}
    </section>
  );
}

/** The diff's lines with long unchanged runs folded to `null`. */
function foldContext(
  lines: readonly GuidelinesSectionDiff['lines'][number][],
): (GuidelinesSectionDiff['lines'][number] | null)[] {
  const near = lines.map(() => false);
  lines.forEach((line, index) => {
    if (line.op === 'same') return;
    for (let i = Math.max(0, index - CONTEXT); i <= Math.min(lines.length - 1, index + CONTEXT); i += 1) near[i] = true;
  });
  const out: (GuidelinesSectionDiff['lines'][number] | null)[] = [];
  lines.forEach((line, index) => {
    if (near[index] === true) out.push(line);
    else if (out.at(-1) !== null) out.push(null);
  });
  return out;
}

/**
 * Each deck's cards with their weights and, for the brand book picked, how
 * often each card was dealt and how often the hand it was in was made.
 */
function DecksView() {
  const [view, setView] = useState<GuidelinesDecksView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [book, setBook] = useState<string>('');

  useEffect(() => {
    const controller = new AbortController();
    fetchGuidelinesDecks(controller.signal)
      .then((next) => {
        setView(next);
        setBook((current) => (current === '' ? (next.brandBooks[0]?.id ?? '') : current));
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <section className="rules-table" aria-labelledby="guidelines-decks-title">
      <header className="rules-table__header">
        <h3 className="rules-table__title" id="guidelines-decks-title">
          <Icon name="stack" /> Decks
        </h3>
        {view !== null && view.brandBooks.length > 0 && (
          <label className="guidelines-decks__book">
            <span className="muted">Brand book</span>{' '}
            <select
              className="field__input field__input--narrow"
              value={book}
              onChange={(event) => {
                setBook(event.target.value);
              }}
            >
              {view.brandBooks.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {view !== null && view.brandBooks.length === 0 && (
        <p className="muted">There is no brand book yet, so no card has been dealt or made.</p>
      )}
      {view === null ? (
        error === null && <Skeleton lines={3} />
      ) : (
        view.decks.map((deck) => (
          <details key={`${deck.audience ?? ''}:${deck.deck}`}>
            <summary>
              <code>{deck.deck}</code>
              {deck.audience !== null && <span className="muted"> ({deck.audience})</span>}{' '}
              <span className="muted">
                {String(deck.cards.length)} card{deck.cards.length === 1 ? '' : 's'}
                {deck.dealtTo !== null && `, dealt to ${deck.dealtTo.join(', ')}`}
              </span>
            </summary>
            {deck.description !== null && <p className="muted">{deck.description}</p>}
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Card</th>
                    <th scope="col">Weight</th>
                    <th scope="col">Dealt</th>
                    <th scope="col">Made</th>
                  </tr>
                </thead>
                <tbody>
                  {deck.cards.map((card) => {
                    const usage = card.usage[book] ?? { dealt: 0, made: 0 };
                    return (
                      <tr key={card.id}>
                        <td>
                          {card.name} <code className="muted">{card.id}</code>
                        </td>
                        <td>{String(card.weight)}</td>
                        <td>{book === '' ? '—' : String(usage.dealt)}</td>
                        <td>{book === '' ? '—' : String(usage.made)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        ))
      )}
    </section>
  );
}
